import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import logoUrl from '#app/assets/logo.svg'
import { EVENTS } from '#app/lib/analytics/events'
import { track } from '#app/lib/analytics/track.client'
import { Button } from './button'

interface ShareDrawerProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	cityName: string
	/** Optional custom title (defaults based on cityName) */
	title?: string
	/** Optional custom description (defaults based on cityName) */
	description?: string
	/** Optional custom share message (defaults based on cityName) */
	message?: string
}

export function ShareDrawer({
	open,
	onOpenChange,
	cityName,
	title,
	description,
	message,
}: ShareDrawerProps) {
	const [showCloseButton, setShowCloseButton] = useState(false)

	// Show close button after 10 seconds
	useEffect(() => {
		if (open) {
			// Track drawer open
			track(EVENTS.SHARE.DRAWER_OPEN, {
				city_name: cityName,
				has_joined: Boolean(cityName),
			})

			const timer = setTimeout(() => {
				setShowCloseButton(true)
			}, 10000)

			return () => clearTimeout(timer)
		} else {
			setShowCloseButton(false)
		}
	}, [open, cityName])

	// Handle escape key (only when close button is visible)
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && showCloseButton) {
				onOpenChange(false)
			}
		}

		if (open) {
			document.addEventListener('keydown', handleEscape)
		}

		return () => {
			document.removeEventListener('keydown', handleEscape)
		}
	}, [open, showCloseButton, onOpenChange])

	// Generate default messages based on whether user has joined (has cityName)
	const hasJoined = Boolean(cityName)

	const defaultTitle = hasJoined
		? 'Thanks for joining the community! ❤️'
		: 'Help us grow the community! ❤️'

	const defaultDescription = hasJoined
		? "Help us grow by sharing on X (Twitter). Here's a message you can easily copy and paste."
		: "Share IndieWorld on X (Twitter) and help us connect indie hackers worldwide. Here's a message you can easily copy and paste."

	const defaultMessage = hasJoined
		? `Just joined IndieWorld from ${cityName}! 🌍 Where are you building from? Add yourself and let's connect!\n\n@indieworldmap`
		: `Check out IndieWorld! 🌍 A global map of indie hackers. See where builders are creating from around the world!\n\n@indieworldmap`
	// Use custom props or fall back to defaults
	const displayTitle = title || defaultTitle
	const displayDescription = description || defaultDescription
	const shareMessage = message || defaultMessage

	const handleTweetNow = () => {
		// Track twitter click
		track(EVENTS.SHARE.TWITTER_CLICK, {
			city_name: cityName,
			has_joined: Boolean(cityName),
		})

		const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`
		window.open(tweetUrl, '_blank', 'noopener,noreferrer')
	}

	const handleCopyMessage = async () => {
		try {
			await navigator.clipboard.writeText(shareMessage)

			// Track link copy
			track(EVENTS.SHARE.LINK_COPY, {
				city_name: cityName,
				has_joined: Boolean(cityName),
			})

			toast.success('Message copied to clipboard!', { id: 'message-copied' })
		} catch {
			toast.error('Failed to copy message', { id: 'copy-failed' })
		}
	}

	const handleClose = () => {
		onOpenChange(false)
	}

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{ x: -100, opacity: 0 }}
					animate={{ x: 0, opacity: 1 }}
					exit={{ x: -100, opacity: 0 }}
					transition={{ duration: 0.3, ease: 'easeOut', delay: 0.5 }}
					className="fixed top-4 z-30 mx-4 max-w-2xl rounded-lg border border-white/10 bg-black/40 p-4 px-4 shadow-[0_0_30px_rgba(139,92,246,0.15)] backdrop-blur-md sm:p-6"
				>
					{/* Close button - shows after 10s */}
					{showCloseButton && (
						<motion.button
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.3 }}
							onClick={handleClose}
							className="absolute top-4 right-4 rounded-lg p-2 transition-colors hover:bg-white/10"
							aria-label="Close"
						>
							<X className="h-5 w-5 text-white" />
						</motion.button>
					)}

					{/* Logo and title */}
					<div className="mb-4 flex items-center">
						<img src={logoUrl} alt="IndieWorld Logo" className="h-12 w-12" />
						<span
							className="text-primary ml-3 text-2xl font-bold"
							style={{ position: 'relative', top: '1px' }}
						>
							Indie World
						</span>
					</div>

					{/* Heading */}
					<h1 className="text-primary mb-2 text-xl font-bold">
						{displayTitle}
					</h1>

					{/* Description */}
					<p className="mb-4 text-sm text-gray-300">{displayDescription}</p>

					{/* Message Preview */}
					<div className="mb-6 rounded-xl border border-white/10 bg-black/20 p-4">
						<p className="text-sm whitespace-pre-line text-white">
							{shareMessage}
						</p>
					</div>

					{/* Action Buttons */}
					<div className="flex flex-col gap-3 sm:flex-row">
						<Button
							onClick={handleTweetNow}
							className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 font-semibold text-white shadow-[0_0_20px_rgba(139,92,246,0.3)] transition-all hover:shadow-[0_0_30px_rgba(139,92,246,0.5)]"
							size="default"
						>
							<svg
								className="mr-2 h-5 w-5 text-white"
								fill="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
							</svg>
							<span className="text-white">Tweet Now</span>
						</Button>
						<Button
							onClick={handleCopyMessage}
							variant="outline"
							className="flex-1"
							size="default"
						>
							<svg
								className="mr-2 h-5 w-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
								/>
							</svg>
							Copy Message
						</Button>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
